<template>
  <div class="c-general-tab__section" v-if="displayFieldMappingSection">
    <h3>Field Mapping</h3>
    <div class="flex gap-8">
      <BittsButton
        v-if="displayMapOwnerButton"
        text="Map Account Owner"
        variant="outline"
        size="small"
        data-testid="map-ae-btn"
        @click="showMapNewColumns"
      />
      <BittsButton
        v-if="displayMapFieldsButton"
        text="Map Fields"
        variant="outline"
        size="small"
        data-testid="map-fields-btn"
        @click="showMapFields"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton } from '@crossbeam/bitts';

import { computed } from 'vue';
import { useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useDataSourceSettings from '@/composables/useDataSourceSettings';
import useFieldMappingVisibility from '@/composables/useFieldMappingVisibility';
import { BIG_CSV_FILE_LIMIT_500MB } from '@/constants/feature_flags';
import {
  DUNS_MDM_PROPERTY,
  REMAPPABLE_ACCOUNT_FIELDS,
} from '@/constants/field_mapping';
import { useFeatureFlagStore, useSourcesStore } from '@/stores';
import { Source } from '@/types/sources';

const router = useRouter();
const sourcesStore = useSourcesStore();

const { hasFeatureFlag } = useFeatureFlagStore();
const { hasPermission } = useAuth();
const canManageDataSources = computed(() =>
  hasPermission('manage:data-sources'),
);

const { isCSVFeed, isGSheetFeed, source, sourceDisabled } =
  useDataSourceSettings();

const hasBigCSVFileLimit = computed(() =>
  hasFeatureFlag(BIG_CSV_FILE_LIMIT_500MB),
);

function hasMappedAEData(source: Source) {
  return sourcesStore
    .getRelatedSources(source)
    .some((source) => source.mdm_type === 'user');
}

const displayMapOwnerButton = computed(
  () =>
    (isCSVFeed.value || isGSheetFeed.value) &&
    !sourceDisabled.value &&
    !(source.value ? hasMappedAEData(source.value) : true) &&
    !hasBigCSVFileLimit.value,
);

const isFileFeed = computed(() => isCSVFeed.value || isGSheetFeed.value);

const allowedMdmProperties = computed(() =>
  isFileFeed.value
    ? REMAPPABLE_ACCOUNT_FIELDS.map((field) => field.mdmProperty)
    : [DUNS_MDM_PROPERTY],
);

const sourceFields = computed(() => source.value?.fields ?? []);
const { displayMapFieldsButton } = useFieldMappingVisibility(
  sourceFields,
  allowedMdmProperties,
);

const displayFieldMappingSection = computed(
  () =>
    canManageDataSources.value &&
    (displayMapOwnerButton.value || displayMapFieldsButton.value),
);

function showMapNewColumns() {
  router.push({
    name: 'map-ae-columns-settings',
  });
}

function showMapFields() {
  if (!source.value?.id) return;
  router.push({
    name: 'map-fields-columns-settings',
    params: {
      source_id: source.value.id.toString(),
    },
  });
}
</script>
